import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Modal, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, Select, SelectItem, Checkbox, Divider,
} from '@heroui/react';
import { ClipboardCopy, ClipboardPaste } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useLineItem, useUpdateLineItem, useLineItems } from '@/api/line-items';
import { useMaterialsLibrary } from '@/api/materials-library';
import { useLineItemCost } from '@/api/calculations';
import { apiClient } from '@/api/client';
import {
  discoverPanelTypes,
  overrideKey,
  isStandardPanel,
  parseOverrides,
  getMaterialIdFromOverride,
  type MaterialOverrideValue,
  type MaterialOverridesMap,
} from './estimate-helpers';

/**
 * Map nesting part labels back to material assignment categories.
 * The part-collector emits specific labels; the material popup groups by category.
 */
const PART_LABEL_TO_CATEGORY: Record<string, string> = {
  'Left Panel': 'Box Panel',
  'Right Panel': 'Box Panel',
  'Bottom Panel': 'Box Panel',
  'Top Panel': 'Box Panel',
  'Stretcher': 'Stretcher',
  'Nailer': 'Stretcher',
  'Middle Stretcher': 'Stretcher',
  'Back Panel': 'Back Panel',
  'Shelf': 'Shelf',
  'Door': 'Door Panel',
  'Drawer Front': 'Door Panel',
  'Drawer Side': 'Drawer Box',
  'Drawer Back': 'Drawer Box',
  'Drawer Bottom': 'Drawer Box',
  'Edgeband': 'Edgeband',
};

function nestingPanelTypesToCategories(panelTypes: string[]): string[] {
  const categories = new Set<string>();
  for (const pt of panelTypes) {
    const cat = PART_LABEL_TO_CATEGORY[pt];
    if (cat) {
      categories.add(cat);
    } else {
      // Unknown panel type — show as-is (frame assembly panels, etc.)
      categories.add(pt);
    }
  }
  return Array.from(categories).sort();
}

interface MaterialsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  lineItemId: string;
  areaId: string;
  projectId: string;
}

export default function MaterialsPopup({
  isOpen, onClose, lineItemId, areaId, projectId,
}: MaterialsPopupProps) {
  const queryClient = useQueryClient();
  const { data: lineItem, isLoading } = useLineItem(isOpen ? lineItemId : null);
  const { data: costData } = useLineItemCost(isOpen ? lineItemId : undefined);
  const { data: materials = [] } = useMaterialsLibrary();
  const { data: areaItems = [] } = useLineItems(isOpen ? areaId : undefined);
  const updateLineItem = useUpdateLineItem();

  const [overrides, setOverrides] = useState<MaterialOverridesMap>({});
  const [clipboard, setClipboard] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [matchDimensions, setMatchDimensions] = useState(false);
  const [bulkCopying, setBulkCopying] = useState(false);

  // Initialize overrides from loaded line item
  useEffect(() => {
    if (lineItem) {
      setOverrides(parseOverrides(lineItem.material_overrides));
    }
  }, [lineItem]);

  // Determine if this is a frame assembly (has freePanels in frame_config)
  const isFrameAssembly = useMemo(() => {
    if (!lineItem) return false;
    const fc = (lineItem as any).frame_config;
    if (!fc) return false;
    try {
      const config = JSON.parse(fc);
      return !!(config.freePanels?.length || config.renderedMeshes?.length);
    } catch { return false; }
  }, [lineItem]);

  // Use nesting-derived panel types when available, fall back to discovery
  const panelTypes = useMemo(() => {
    if (costData?.nesting_panel_types && costData.nesting_panel_types.length > 0) {
      // Frame assemblies: show actual panel types as-is (no category grouping)
      if (isFrameAssembly) {
        return [...new Set(costData.nesting_panel_types)].sort();
      }
      return nestingPanelTypesToCategories(costData.nesting_panel_types);
    }
    return lineItem ? discoverPanelTypes(lineItem as any) : [];
  }, [lineItem, costData, isFrameAssembly]);

  // Lookup material by ID from the library cache
  const materialById = useMemo(() => {
    const map = new Map<string, any>();
    for (const m of materials) map.set(m.id, m);
    return map;
  }, [materials]);

  // Get the assigned material for a panel type (from overrides or FK fallback)
  const getMaterialForPanel = useCallback(
    (panelType: string) => {
      const key = overrideKey(panelType);
      const ov = overrides[key];
      const matId = getMaterialIdFromOverride(ov);
      if (matId) return materialById.get(matId) || null;

      // FK fallback for standard panel types
      if (!lineItem) return null;
      const fkMap: Record<string, string | null | undefined> = {
        'Box Panel': (lineItem as any).box_material_id,
        'Door Panel': (lineItem as any).door_material_id,
        'Back Panel': (lineItem as any).back_material_id,
        'Shelf': (lineItem as any).shelf_material_id,
        'Edgeband': (lineItem as any).edgeband_material_id,
      };
      const fkId = fkMap[panelType];
      return fkId ? materialById.get(fkId) || null : null;
    },
    [overrides, materialById, lineItem],
  );

  function handleChangeMaterial(panelType: string, materialId: string | null) {
    const key = overrideKey(panelType);
    setOverrides((prev) => {
      const copy = { ...prev };
      if (materialId) {
        // Standard panels: store plain UUID string (resolver-compatible for V2 calc)
        // Frame panels: store object with materialId (Python _frame_ convention)
        copy[key] = isStandardPanel(panelType)
          ? materialId
          : { materialId, composite: null };
      } else {
        delete copy[key];
      }
      return copy;
    });
  }

  function handleCopy(panelType: string) {
    const key = overrideKey(panelType);
    const ov = overrides[key];
    const matId = getMaterialIdFromOverride(ov);
    if (matId) {
      setClipboard(matId);
      toast.success(`Copied ${panelType} material`);
    } else {
      toast('No material to copy', { description: 'Assign a material first' });
    }
  }

  function handlePaste(panelType: string) {
    if (!clipboard) return;
    handleChangeMaterial(panelType, clipboard);
    toast.success(`Pasted material to ${panelType}`);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await updateLineItem.mutateAsync({
        id: lineItemId,
        material_overrides: JSON.stringify(overrides),
      });
      toast.success('Materials updated');
      onClose();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update materials');
    } finally {
      setSaving(false);
    }
  }

  // Filter matching target items for bulk copy
  function getMatchingItems(items: any[]): any[] {
    if (!lineItem) return [];
    const isFrame = !!(lineItem as any).frame_template_id;
    return items.filter((item: any) => {
      if (item.id === lineItemId) return false;
      if (isFrame) {
        // For frame assemblies: match any other frame assembly item
        // (they share the same panel type system; only matching overrides will apply)
        if (!item.frame_template_id) return false;
      } else {
        // For cabinet template items: match exact template
        const sameTemplate =
          (lineItem as any).template_id && item.template_id === (lineItem as any).template_id;
        if (!sameTemplate) return false;
      }
      // Optional dimension matching
      if (matchDimensions) {
        const w = Math.round(((lineItem as any).width || 0) * 10000);
        const h = Math.round(((lineItem as any).height || 0) * 10000);
        const d = Math.round(((lineItem as any).depth || 0) * 10000);
        if (
          Math.round((item.width || 0) * 10000) !== w ||
          Math.round((item.height || 0) * 10000) !== h ||
          Math.round((item.depth || 0) * 10000) !== d
        ) return false;
      }
      return true;
    });
  }

  async function handleBulkCopy(scope: 'area' | 'project') {
    setBulkCopying(true);
    try {
      let targets: any[] = [];
      const overridesJson = JSON.stringify(overrides);

      if (scope === 'area') {
        targets = getMatchingItems(areaItems);
      } else {
        // Fetch all areas in project, then line items per area
        const { data: areasData } = await apiClient.get<any>(`/projects/${projectId}/areas`);
        const areas: any[] = Array.isArray(areasData) ? areasData : areasData?.areas ?? [];
        const allItems: any[] = [];
        await Promise.all(
          areas.map(async (area: any) => {
            const { data: items } = await apiClient.get<any[]>('/line-items', { params: { area_id: area.id } });
            allItems.push(...items);
          }),
        );
        targets = getMatchingItems(allItems);
      }

      if (targets.length === 0) {
        toast('No matching items found', { description: 'No items with the same template type' });
        setBulkCopying(false);
        return;
      }

      // Apply overrides to each target, only for panel types that exist on the target
      let updated = 0;
      await Promise.all(
        targets.map(async (target: any) => {
          // Build set of valid panel types for this target
          // Use server-side cost data (nesting_panel_types) if available, else parse frame_config
          const targetPanelTypes = new Set<string>();
          try {
            const costResp = await apiClient.get(`/calculations/line-item/${target.id}/cost`);
            const costData = costResp.data as any;
            if (costData?.nesting_panel_types) {
              for (const pt of costData.nesting_panel_types) targetPanelTypes.add(pt);
            }
          } catch { /* ignore — fall through to frame_config parse */ }
          if (targetPanelTypes.size === 0 && target.frame_config) {
            try {
              const fc = typeof target.frame_config === 'string' ? JSON.parse(target.frame_config) : target.frame_config;
              for (const fp of fc?.freePanels || []) {
                const pt = fp?.panelType || fp?.partType;
                if (pt) targetPanelTypes.add(pt);
              }
              for (const rm of fc?.renderedMeshes || []) {
                const pt = rm?.panelType || rm?.partType;
                if (pt) targetPanelTypes.add(pt);
              }
            } catch { /* ignore */ }
          }

          const targetOverrides = parseOverrides(target.material_overrides);
          const targetIsFrame = !!target.frame_template_id;
          let changed = false;
          for (const [key, value] of Object.entries(overrides)) {
            if (targetIsFrame) {
              // Frame assemblies: only copy _frame_ keys, skip legacy FK keys
              if (!key.startsWith('_frame_')) continue;
              const pt = key.replace('_frame_', '');
              if (targetPanelTypes.size > 0 && !targetPanelTypes.has(pt)) continue;
            }
            targetOverrides[key] = structuredClone(value);
            changed = true;
          }
          if (changed) {
            await apiClient.patch(`/line-items/${target.id}`, {
              material_overrides: JSON.stringify(targetOverrides),
            });
            updated++;
          }
        }),
      );

      // Invalidate caches so table reflects updated data
      queryClient.invalidateQueries({ queryKey: ['line-items'] });
      queryClient.invalidateQueries({ queryKey: ['calculations'] });
      toast.success(`Materials copied to ${updated} item${updated !== 1 ? 's' : ''}`);
    } catch (err: any) {
      toast.error(err?.message || 'Bulk copy failed');
    } finally {
      setBulkCopying(false);
    }
  }

  const title = lineItem
    ? `Materials — ${(lineItem as any).cabinet_code || ''} ${lineItem.description}`.trim()
    : 'Materials';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" classNames={{ base: 'bg-[var(--bg-surface)]' }}>
      <ModalContent>
        <ModalHeader className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
          {title}
        </ModalHeader>
        <ModalBody>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-6 w-6 border-2 border-[var(--accent)] border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {/* Header row */}
              <div className="grid gap-2 px-1 py-1 text-xs font-semibold"
                style={{ gridTemplateColumns: '140px 1fr 70px 90px auto', color: 'var(--text-secondary)' }}>
                <span>Panel Type</span>
                <span>Material</span>
                <span>Thickness</span>
                <span>Cost/Unit</span>
                <span className="text-center">Actions</span>
              </div>

              {panelTypes.map((pt) => {
                const mat = getMaterialForPanel(pt);
                return (
                  <div
                    key={pt}
                    className="grid gap-2 items-center px-1 py-1.5 rounded hover:bg-[var(--bg-hover)]"
                    style={{ gridTemplateColumns: '140px 1fr 70px 90px auto' }}
                  >
                    <span className="text-xs truncate" style={{ color: 'var(--text-primary)' }}>
                      {pt}
                    </span>

                    <Select
                      aria-label={`Material for ${pt}`}
                      selectedKeys={getMaterialIdFromOverride(overrides[overrideKey(pt)]) ? [getMaterialIdFromOverride(overrides[overrideKey(pt)])!] : (mat ? [mat.id] : [])}
                      onSelectionChange={(keys) => {
                        const val = Array.from(keys)[0] as string;
                        handleChangeMaterial(pt, val ?? null);
                      }}
                      size="sm"
                      placeholder="-- Not Assigned --"
                      classNames={{ trigger: 'bg-[var(--bg-panel)] border-[var(--border)] text-xs h-8' }}
                    >
                      {(materials as any[]).map((m) => (
                        <SelectItem key={m.id}>{m.name}</SelectItem>
                      ))}
                    </Select>

                    <span className="text-xs text-right" style={{ color: mat ? 'var(--text-secondary)' : 'var(--text-muted)' }}>
                      {mat?.thickness != null ? `${mat.thickness}"` : '—'}
                    </span>

                    <span className="text-xs text-right" style={{ color: mat ? 'var(--text-secondary)' : 'var(--text-muted)' }}>
                      {mat?.cost_per_unit != null ? `$${Number(mat.cost_per_unit).toFixed(2)}` : '—'}
                    </span>

                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => handleCopy(pt)}
                        className="p-1 rounded hover:bg-[var(--bg-hover)]"
                        title="Copy material"
                      >
                        <ClipboardCopy size={13} style={{ color: 'var(--text-muted)' }} />
                      </button>
                      <button
                        onClick={() => handlePaste(pt)}
                        className="p-1 rounded hover:bg-[var(--bg-hover)]"
                        title="Paste material"
                        disabled={!clipboard}
                        style={{ opacity: clipboard ? 1 : 0.3 }}
                      >
                        <ClipboardPaste size={13} style={{ color: 'var(--text-muted)' }} />
                      </button>
                    </div>
                  </div>
                );
              })}

              {panelTypes.length === 0 && (
                <p className="text-xs text-center py-4" style={{ color: 'var(--text-muted)' }}>
                  No panel types found for this item
                </p>
              )}

              {/* Bulk copy section */}
              <Divider className="my-2" />
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>
                  Copy materials to:
                </span>
                <Button
                  size="sm"
                  variant="flat"
                  onPress={() => handleBulkCopy('area')}
                  isLoading={bulkCopying}
                  isDisabled={saving}
                >
                  Area
                </Button>
                <Button
                  size="sm"
                  variant="flat"
                  onPress={() => handleBulkCopy('project')}
                  isLoading={bulkCopying}
                  isDisabled={saving}
                >
                  Project
                </Button>
                <Checkbox
                  size="sm"
                  isSelected={matchDimensions}
                  onValueChange={setMatchDimensions}
                  classNames={{ label: 'text-xs' }}
                >
                  Match dimensions
                </Checkbox>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="light" onPress={onClose}>Cancel</Button>
          <Button
            size="sm"
            color="primary"
            onPress={handleSave}
            isLoading={saving}
            isDisabled={isLoading}
          >
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
